import Cookies from 'js-cookie';
const CART_KEY = 'cartItems';
const dispatchCartUpdate = () => {
    window.dispatchEvent(new Event('cartUpdated'));
};
// The cart is always the acting user's own (Cart.user, server-side) - an admin ordering for a
// clinic (see ImpersonationContext) does not get a clinic-scoped cart, so no ?clinic_id= or
// ?user_id= is added here. See ClinicDetails' Create Order handler, which clears the cart
// before starting a clinic order so items can never bleed across clinics.
const api = {
    async getCart() {
        try {
            const url = '/api/v1/cart/';
            const response = await fetch(url, {
                credentials: 'include',
            });
            if (!response.ok)
                throw new Error('Failed to fetch cart');
            const data = await response.json();
            return (data.items?.map((item) => ({
                id: item.product,
                name: item.productTitle,
                sku: item.productSku,
                price: parseFloat(item.productPrice),
                quantity: item.quantity,
                image: item.productImageUrl,
            })) || []);
        }
        catch (err) {
            console.error('Failed to fetch cart from API', err);
            return [];
        }
    },
    async addToCart(item) {
        try {
            const url = '/api/v1/cart/add_item/';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                credentials: 'include',
                body: JSON.stringify({
                    product_id: item.id,
                    quantity: item.quantity || 1,
                }),
            });
            if (!response.ok) {
                throw new Error(`Failed to add to cart: ${response.status}`);
            }
        }
        catch (err) {
            console.error('Failed to add item to cart via API', err);
            throw err;
        }
    },
    async clearCart() {
        try {
            const url = '/api/v1/cart/clear/';
            await fetch(url, {
                method: 'DELETE',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                credentials: 'include',
            });
        }
        catch (err) {
            console.error('Failed to clear cart via API', err);
            throw err;
        }
    },
    async removeItem(productId) {
        try {
            const url = '/api/v1/cart/remove_item/';
            await fetch(url, {
                method: 'DELETE',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    product_id: productId,
                }),
            });
        }
        catch (err) {
            console.error('Failed to clear cart via API', err);
            throw err;
        }
    },
    async updateItem(productId, quantity) {
        try {
            const url = '/api/v1/cart/update_item/';
            await fetch(url, {
                method: 'PUT',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    product_id: productId,
                    quantity: quantity,
                }),
            });
        }
        catch (err) {
            console.error('Failed to update cart item via API', err);
            throw err;
        }
    },
};
const validateCartItems = async (cartItems) => {
    if (cartItems.length === 0)
        return cartItems;
    try {
        const url = '/api/v1/products/';
        const response = await fetch(url, {
            credentials: 'include',
        });
        if (!response.ok) {
            return cartItems;
        }
        const data = await response.json();
        const availableProducts = data.results || data;
        const availableProductIds = new Set(availableProducts
            .filter((p) => p.isAvailable && p.isActive)
            .map((p) => p.id.toString()));
        const validItems = cartItems.filter((item) => availableProductIds.has(item.id));
        if (validItems.length !== cartItems.length) {
            localStorageApi.saveCart(validItems);
        }
        return validItems;
    }
    catch (err) {
        console.error('Failed to validate cart items:', err);
        return cartItems;
    }
};
const localStorageApi = {
    getCart() {
        try {
            const cartContents = localStorage.getItem(CART_KEY);
            if (!cartContents)
                return [];
            return JSON.parse(cartContents);
        }
        catch (err) {
            console.error('Failed to parse cart from localStorage', err);
            return [];
        }
    },
    saveCart(cart) {
        try {
            localStorage.setItem(CART_KEY, JSON.stringify(cart));
            dispatchCartUpdate();
        }
        catch (err) {
            console.error('Failed to save cart to localStorage', err);
        }
    },
    addToCart(item) {
        try {
            const cart = this.getCart();
            const existing = cart.find((i) => i.id === item.id);
            if (existing) {
                existing.quantity += item.quantity || 1;
            }
            else {
                cart.push({ ...item, quantity: item.quantity || 1 });
            }
            this.saveCart(cart);
        }
        catch (err) {
            console.error('Failed to add item to cart', err);
        }
    },
    clearCart() {
        try {
            localStorage.removeItem(CART_KEY);
            dispatchCartUpdate();
        }
        catch (err) {
            console.error('Failed to clear cart from localStorage', err);
        }
    },
};
export const getCart = async (isAuthenticated = false) => {
    if (isAuthenticated) {
        return await api.getCart();
    }
    else {
        const cart = localStorageApi.getCart();
        return await validateCartItems(cart);
    }
};
export const addToCart = async (item, isAuthenticated = false) => {
    try {
        const response = await fetch(`/api/v1/products/${item.id}/`, {
            credentials: 'include',
        });
        if (!response.ok) {
            return;
        }
        const product = await response.json();
        if (!product.isAvailable) {
            return;
        }
    }
    catch (err) {
        console.error('Failed to validate product before adding to cart:', err);
    }
    if (isAuthenticated) {
        await api.addToCart(item);
        dispatchCartUpdate();
    }
    else {
        localStorageApi.addToCart(item);
    }
};
export const clearCart = async (isAuthenticated = false) => {
    if (isAuthenticated) {
        await api.clearCart();
        dispatchCartUpdate();
    }
    else {
        localStorageApi.clearCart();
    }
};
export const removeFromCart = async (productId, isAuthenticated = false) => {
    if (isAuthenticated) {
        await api.removeItem(productId);
    }
    else {
        const cart = localStorageApi.getCart();
        const updatedCart = cart.filter((item) => item.id !== productId);
        localStorageApi.saveCart(updatedCart);
    }
};
export const updateCartItemQuantity = async (productId, quantity, isAuthenticated = false) => {
    if (isAuthenticated) {
        await api.updateItem(productId, quantity);
    }
    else {
        const cart = localStorageApi.getCart();
        const updatedCart = cart.map((item) => (item.id === productId ? { ...item, quantity } : item));
        localStorageApi.saveCart(updatedCart);
    }
};
export const saveCart = async (cart, isAuthenticated = false) => {
    if (isAuthenticated) {
        return;
    }
    else {
        localStorageApi.saveCart(cart);
    }
};
export const migrateGuestCartToUser = async (isAuthenticated = false) => {
    if (!isAuthenticated)
        return;
    try {
        const localCart = localStorageApi.getCart();
        if (localCart.length > 0) {
            for (const item of localCart) {
                await api.addToCart(item);
            }
            dispatchCartUpdate();
        }
    }
    catch (err) {
        console.error('Failed to migrate cart to user account:', err);
    }
};
